// lib
import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { getMethodOptions } from '@aytm/app/stats/page/question-views/components/mapping/lib'
import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/helpers'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const Attribute = ({ item, position, dimension, dndItemsList, onMethodFocus, onMethodBlur }) => {
  const { id, mode, data, disabled, attributesOptions = [], dispatch } = useContext(DataContext)

  const [methodsType, setMethodsType] = useState(null)
  const [methodsSubtype, setMethodsSubtype] = useState(null)

  const [selectedValue, setSelectedValue] = useState(null)

  const [itemMode, setItemMode] = useState(mode)

  const [input, setInput] = useState(item.refs)
  const [focused, setFocused] = useState(false)

  const timer = useRef(null)

  const changedRef = useRef(false)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const refsRef = useRef(null)
  refsRef.current = item.refs

  const focusedRef = useRef(focused)
  focusedRef.current = focused

  const methodRef = useRef(null)
  const methodValueRef = useRef(item.method)

  const methodsTypeRef = useRef(null)
  methodsTypeRef.current = methodsType

  const methodsSubtypeRef = useRef(null)
  methodsSubtypeRef.current = methodsSubtype

  const methodsOptions = useMemo(() => {
    const refs = item.refs
      .split(',')
      .map(v => v.trim())
      .slice(0, dimension)

    const { type, subtype, methods } = getMethodOptions(refs, data.questions)

    setMethodsType(type)
    setMethodsSubtype(subtype)

    return methods
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.refs, dimension, data.questions])

  const onMethodChange = useCallback(
    (methods, applyToAll = false) => {
      let method = methods
      if (Array.isArray(methods)) {
        method.sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
        method = method.join(',')
      }

      if (applyToAll)
        dispatch({
          type: 'UPDATE_ATTRIBUTES_METHOD',
          data: {
            method: method
          }
        })
      else
        dispatch({
          type: 'UPDATE_ATTRIBUTE',
          data: {
            item: {
              ...item,
              method: method
            },
            position: position - 1
          }
        })
    },
    [item, position, dispatch]
  )

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: data.windowUID,
      remove: () =>
        dispatch({
          type: 'DELETE_ATTRIBUTE',
          data: position - 1
        })
    }
  }, [item, position, id, data.windowUID, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-attribute-${data.windowUID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-attribute-${data.windowUID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className={option.item ? '' : 'p-item-label'}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const buttonLabelTemplate = event => {
    const methods =
      event != undefined && event.value != undefined && 0 < event.value.length
        ? methodsOptions.filter(o => event.value.includes(o.key))
        : []

    const text =
      0 < methods.length
        ? methods.length == methodsOptions.length
          ? 'All'
          : 1 == methods.length
            ? methods[0].label
            : methods
                .map(o => o.key)
                .join(',')
                .toUpperCase()
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text }}></span>
  }

  const onInputChange = event => setInput(event.target.value || '')

  const onInputFocus = () => setFocused(true)
  const onInputBlur = () => setFocused(false)

  const onSelectedChange = value => {
    if (value === refsRef.current) return

    const option = attributesOptions.find(v => v.key == value)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ATTRIBUTE',
      data: {
        item: {
          ...item,
          text: option ? option.label : item.text,
          refs: value || ''
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ATTRIBUTE',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ATTRIBUTE',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ATTRIBUTE',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)

    focusedRef.current &&
      (timer.current = setTimeout(() => {
        onSelectedChange(input)
      }, 500))

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  useEffect(() => {
    if (changedRef.current) {
      dispatch({
        type: 'UPDATE_ATTRIBUTE',
        data: {
          item: {
            ...item,
            method: 0 < methodsOptions.length ? methodsOptions[0].key : ''
          },
          position: position - 1
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodsType, methodsSubtype])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error:
          null === item.method || !item.method.length || !item.refs.split(',').filter(v => 0 < v.trim().length).length
      }
    })
  }, [item.method, item.refs, position, dispatch])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)

    const refs = item.refs
      ? item.refs
          .split(',')
          .map(v => v.trim())
          .join(', ')
      : ''

    setSelectedValue(item.refs ? refs : null)

    if (!focusedRef.current) setInput(refs)

    changedRef.current = false
  }, [item.refs])

  useEffect(() => {
    if (!methodsOptions.length) {
      if (null !== item.method && 0 < item.method.length) onMethodChange('')
    } else {
      if (
        ['rb', 'mx', 'sr', 'se'].includes(methodsTypeRef.current) ||
        ('mp' == methodsTypeRef.current && 'radios' == methodsSubtypeRef.current)
      ) {
        if (!methodsOptions.some(option => option.key === item.method)) {
          onMethodChange(
            methodsOptions.some(option => option.key === methodValueRef.current)
              ? methodValueRef.current
              : methodsOptions[0].key
          )
        } else methodValueRef.current = item.method
      } else {
        if (0 < item.method.length) {
          const methods = item.method.split(',')
          if (methods.some(method => !methodsOptions.some(option => option.key === method))) {
            onMethodChange(
              methodValueRef.current.split(',').some(method => methodsOptions.some(option => option.key === method))
                ? methodValueRef.current
                    .split(',')
                    .filter(method => methodsOptions.some(option => option.key === method))
                    .join(',')
                : methodsOptions.map(option => option.key).join(',')
            )
          } else methodValueRef.current = item.method
        } else methodValueRef.current = item.method
      }
    }
  }, [item.method, methodsOptions, onMethodChange])

  useEffect(() => {
    const isAdvanced =
      'advanced' == mode ||
      !attributesOptions.length ||
      (null !== selectedValue && !attributesOptions.some(v => v.key == selectedValue))

    setItemMode(isAdvanced ? 'advanced' : mode)
  }, [mode, attributesOptions, selectedValue])

  const renderAdvanced = () => (
    <InputText
      className={`${0 < input.length ? '' : 'p-inputtext-empty'}${
        !focused && 0 < input.length && !methodsOptions.length ? 'p-invalid' : ''
      }`}
      placeholder={'Please type question numbers (ex: "Q1, Q3, Q5...")'}
      value={input}
      disabled={disabled}
      onChange={disabled ? null : onInputChange}
      onFocus={disabled ? null : onInputFocus}
      onBlur={disabled ? null : onInputBlur}
    />
  )
  const renderBasic = () => (
    <Dropdown
      placeholder="Select..."
      value={selectedValue}
      options={attributesOptions}
      optionValue="key"
      optionLabel="text"
      itemTemplate={itemTemplate}
      valueTemplate={valueTemplate}
      filter={true}
      filterBy="search,text"
      filterPlaceholder="Search items"
      resetFilterOnHide={true}
      filterInputAutoFocus={false}
      fixedDropdownDir="bottom"
      disabled={disabled}
      onChange={disabled ? null : event => onSelectedChange(event.value)}
    />
  )

  const renderComponent = () => (
    <>
      <div className={`${style.index} p-field`}>{position}.</div>
      <div
        className={`${style.group} ${style['group-av']}${
          disabled ? ' ' + style['group-av-disabled'] : ''
        } p-fluid p-field p-col`}
      >
        <div className="p-fluid p-field p-col-6">
          <div className="p-field p-col-12">
            {disabled || 'advanced' == itemMode ? renderAdvanced() : renderBasic()}
          </div>
        </div>
        <div ref={methodRef} className={`${style.group} ${style['group-composite']} p-fluid p-field p-col-6`}>
          <div className={`p-field p-col-5`}>
            {!methodsOptions.length ? (
              <MultiSelect
                className="p-composite"
                placeholder="Select..."
                fixedDropdownDir="bottom"
                appendTo={methodRef.current || 'self'}
                disabled={true}
              />
            ) : ['rb', 'mx', 'sr', 'se'].includes(methodsType) ||
              ('mp' == methodsType && 'radios' == methodsSubtype) ? (
              <Dropdown
                className="p-composite"
                placeholder="Select..."
                value={item.method}
                options={methodsOptions}
                optionValue="key"
                optionLabel="text"
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                fixedDropdownDir="bottom"
                appendTo={methodRef.current || 'self'}
                disabled={disabled}
                onChange={
                  disabled
                    ? null
                    : event =>
                        onMethodChange(
                          event.value,
                          event.originalEvent &&
                            event.originalEvent.shiftKey &&
                            !event.originalEvent.ctrlKey &&
                            !event.originalEvent.altKey &&
                            !event.originalEvent.metaKey
                        )
                }
                onFocus={onMethodFocus}
                onBlur={onMethodBlur}
              />
            ) : (
              <MultiSelect
                className="p-composite"
                placeholder="Select..."
                value={0 < item.method.length ? item.method.split(',') : []}
                options={methodsOptions}
                optionValue="key"
                optionLabel="text"
                itemTemplate={itemTemplate}
                buttonLabelTemplate={buttonLabelTemplate}
                panelHeaderTemplate={MultiSelectHeader}
                panelFooterTemplate={MultiSelectFooter}
                fixedDropdownDir="bottom"
                appendTo={methodRef.current || 'self'}
                disabled={disabled}
                onChange={
                  disabled
                    ? null
                    : event =>
                        onMethodChange(
                          event.value,
                          event.originalEvent &&
                            event.originalEvent.shiftKey &&
                            !event.originalEvent.ctrlKey &&
                            !event.originalEvent.altKey &&
                            !event.originalEvent.metaKey
                        )
                }
                onFocus={onMethodFocus}
                onBlur={onMethodBlur}
              />
            )}
          </div>
          <div className="p-field p-col">
            <InputText
              className="p-composite"
              placeholder="Please type a label..."
              value={decodeText(item.text)}
              maxLength={120}
              disabled={disabled}
              onChange={disabled ? null : onLabelChange}
            />
          </div>
        </div>
      </div>
      {disabled ? null : (
        <div className="p-field">
          <span ref={dndListItemHandleRef} className={style.handle} onClick={disabled ? null : onHandleClick}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
    </>
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']} p-fluid p-col-12`}>
      {disabled ? null : (
        <span className={style.delete} onClick={disabled ? null : onDeleteClick}>
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <Popup
        targetClassName="p-fluid p-field p-col-12"
        width="254px"
        trigger={0 < item.refs.length && !methodsOptions.length ? 'hover' : 'none'}
        direction="topleft"
        popupOffset={46}
        show={0 < item.refs.length && !methodsOptions.length ? null : false}
        hideOtherPopupsOnShow
        content={() => <p>Please list corresponding questions/subquestions for each run separated by commas.</p>}
      >
        {renderComponent()}
      </Popup>
    </div>
  )
}

Attribute.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dimension: PropTypes.number,
  dndItemsList: PropTypes.any,
  onMethodFocus: PropTypes.func,
  onMethodBlur: PropTypes.func
}

Attribute.displayName = 'Attribute'

export default Attribute
